import { formatCount, tt } from '@agos/shared'

/**
 * Two log viewers — who changed what, and what broke.
 *
 * **FB-107.** `/system-logs` is a flat chronological list, and the lines that matter are buried in it.
 * Of the newest thousand rows: **926 are `info`, 59 are errors and 15 are warnings** — so 7 % of the
 * screen is the part somebody opened the page for. And the thousand rows are only **57 distinct
 * messages**: 259 of them are *"Fiskálny doklad vytlačený – Rozvoz"* with different numbers in it, 211
 * the same for card-online, 153 a card payment on the restaurant till. A page called *what broke* that
 * shows a receipt printing four hundred times is not answering its own question.
 *
 * Grouped by shape — digits replaced, so one message with a different order number is one row — the
 * thousand becomes fifty-seven, the errors are countable, and the recurring failure is visible. The
 * newest occurrence of each is kept so the raw line is still there to read.
 *
 * **FB-108.** `/audit-logs` holds **79 700 rows** and answers `total` alongside a `limit`/`offset`
 * page. `/system-logs` takes `level`, `source`, `deviceId`, `since` and `limit` — **no offset, and no
 * total**, so it cannot be paged at all: the only way to see further back is to ask for more rows.
 * Legacy pages the first and not the second, and neither page says which it is doing. Both say so here.
 */

// ─── who changed what ────────────────────────────────────────────────────────

export interface AuditRow {
  id?: number
  employeeId?: number | null
  employeeName?: string | null
  role?: string | null
  action?: string
  entityType?: string
  entityId?: string | null
  detail?: Record<string, unknown> | null
  createdAt?: string | null
}

export interface AuditListing {
  total?: number
  rows?: AuditRow[]
}

/** Postgres prints `2026-07-29 14:46:35+00`; the API prints an ISO instant. Both parse here. */
export function parseStamp(value: string | null | undefined): number | null {
  const text = (value ?? '').trim()
  if (text === '') return null
  const parsed = Date.parse(text.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'))
  return Number.isFinite(parsed) ? parsed : null
}

export function formatStamp(value: string | null | undefined): string {
  const stamp = parseStamp(value)
  if (stamp === null) return '-'
  return new Date(stamp).toLocaleString('sk-SK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

const ENTITY_LABELS: Record<string, string> = {
  order: tt('Objednávka'),
  order_tab: tt('Otvorený účet'),
  product: tt('Produkt'),
  employee: tt('Zamestnanec'),
  coupon: tt('Kupón'),
  settings: tt('Nastavenia'),
  customer: tt('Zákazník'),
  stock_item: tt('Skladová položka'),
  stock_receipt: tt('Príjemka'),
}

export function entityLabel(entityType: string | undefined): string {
  return ENTITY_LABELS[entityType ?? ''] ?? (entityType ?? '')
}

/**
 * **FB-109.** Legacy builds the entity filter from its own five-entry label map, so three of the eight
 * kinds the log actually holds — `stock_item` (17 rows), `stock_receipt` (3) and `order_tab` (3) — are
 * unreachable: the dropdown has no option for them, and 79 700 rows is far too many to page through by
 * hand. The catalogue below is what the log contains; the page adds anything else the loaded page shows,
 * so a kind added tomorrow is selectable without a code change.
 */
export const ENTITY_TYPES: readonly string[] = [
  'order',
  'settings',
  'employee',
  'product',
  'coupon',
  'stock_item',
  'order_tab',
  'stock_receipt',
]

export function entityOptions(rows: AuditRow[]): { value: string; label: string }[] {
  const seen = new Set<string>(ENTITY_TYPES)
  for (const row of rows) {
    const type = row.entityType ?? ''
    if (type !== '') seen.add(type)
  }
  return [...seen]
    .map((type) => ({ value: type, label: entityLabel(type) }))
    .sort((left, right) => left.label.localeCompare(right.label, 'sk'))
}

const ACTION_LABELS: Record<string, string> = {
  create: tt('Vytvorenie'),
  update: tt('Úprava'),
  delete: tt('Zmazanie'),
  login: tt('Prihlásenie'),
  // Legacy has none of these four, so they read as `settle_tab` and `return_to_kitchen` on screen.
  settle_tab: tt('Vyrovnanie účtu'),
  append_to_tab: tt('Pridanie na účet'),
  open_tab: tt('Otvorenie účtu'),
  return_to_kitchen: tt('Vrátenie do kuchyne'),
}

const STATUS_LABELS: Record<string, string> = {
  new: tt('Nová'),
  confirmed: tt('Potvrdená'),
  preparing: tt('Príprava'),
  quality_check: tt('Kontrola kvality'),
  ready: tt('Pripravená'),
  on_the_way: tt('Na ceste'),
  at_address: tt('Na adrese'),
  delivered: tt('Doručená'),
  picked_up: tt('Vyzdvihnutá'),
  cancelled: tt('Zrušená'),
}

/**
 * What an action says it did.
 *
 * **FB-109.** Legacy lists every `status_change:*` and `confirm_status:*` pair by hand — sixteen
 * entries, three of which match nothing in the log — and falls through to the raw key for anything
 * else. Five actions on eleven rows read as snake_case on screen: `status_change:confirmed` (3),
 * `return_to_kitchen` (3), `settle_tab` (2), `append_to_tab` (2), `open_tab` (1). Splitting on the
 * colon covers every present and future status: the prefix names the kind of act and the suffix is a
 * status the orders module already labels.
 */
export function actionLabel(action: string | undefined): string {
  const raw = action ?? ''
  const known = ACTION_LABELS[raw]
  if (known !== undefined) return known

  const colon = raw.indexOf(':')
  if (colon > 0) {
    const kind = raw.slice(0, colon)
    const status = STATUS_LABELS[raw.slice(colon + 1)] ?? raw.slice(colon + 1)
    if (kind === 'status_change') return `${tt('Stav')} → ${status}`
    if (kind === 'confirm_status') return `${tt('Potvrdenie')}: ${status}`
    return `${kind}: ${status}`
  }
  return raw
}

export type BadgeColor = 'zinc' | 'blue' | 'emerald' | 'red' | 'amber' | 'violet'

export function actionColor(action: string | undefined): BadgeColor {
  const raw = action ?? ''
  if (raw === 'delete' || raw.endsWith(':cancelled')) return 'red'
  if (raw.startsWith('status_change:') || raw.startsWith('confirm_status:')) return 'amber'
  if (raw === 'create') return 'emerald'
  if (raw === 'update') return 'blue'
  if (raw === 'login') return 'violet'
  return 'zinc'
}

/**
 * Moved to `@agos/shared` for §9.257, and re-exported here so this file's callers and tests are
 * untouched. The manager **shell** needs the same mapping and cannot import a module (§5); it printed
 * the raw key instead, on every screen.
 */
export { roleLabel } from '@agos/shared'

/** One scalar of a detail blob as text. */
function detailText(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value) ?? ''
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isScalar(value: unknown): boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
}

/**
 * The interesting fields of a detail blob, as text - §9.380, FB-675.
 *
 * **The two comments in this file used to contradict each other**: `detailText` said *"a nested
 * object as JSON"* and this one said *"without printing the JSON at somebody"*, and the second is
 * what the screen promises and what its e2e asserts. An order update writes
 * `{"changed": {"total": 9.9}, "itemsChanged": true}`, so the panel printed `{"total":9.9}` at
 * whoever opened it - and it took a write spec landing that row at the top of the log for anything
 * to notice.
 *
 * **One level in, and no deeper.** Every blob this system writes is one level deep - `changed`,
 * `before`, `after` - so `changed.total` is a field somebody can read. A deeper value keeps the JSON
 * fallback, which is a floor rather than a promise: printing `[object Object]` would be worse.
 */
export function detailPairs(detail: Record<string, unknown> | null | undefined): [string, string][] {
  if (detail === null || detail === undefined) return []
  const pairs: [string, string][] = []
  for (const [key, value] of Object.entries(detail)) {
    if (value === null || value === undefined || value === '') continue
    if (isPlainObject(value)) {
      for (const [innerKey, innerValue] of Object.entries(value)) {
        if (innerValue === null || innerValue === undefined || innerValue === '') continue
        pairs.push([`${key}.${innerKey}`, detailText(innerValue)])
      }
      continue
    }
    if (Array.isArray(value)) {
      // A list of scalars reads as a list. A list of objects is past what a two-column panel can
      // show, so it keeps the fallback.
      if (value.length === 0) continue
      if (value.every(isScalar)) {
        pairs.push([key, value.map(detailText).join(', ')])
        continue
      }
      pairs.push([key, detailText(value)])
      continue
    }
    pairs.push([key, detailText(value)])
  }
  return pairs
}

export interface AuditFilter {
  employeeId: string
  entityType: string
  dateFrom: string
  dateTo: string
  search: string
}

export const EMPTY_AUDIT_FILTER: AuditFilter = {
  employeeId: 'all',
  entityType: 'all',
  dateFrom: '',
  dateTo: '',
  search: '',
}

export const AUDIT_PAGE_SIZE = 50

/** Exactly the parameters `GET /api/v1/audit-logs` declares, so a typo cannot compile. */
export interface AuditQuery {
  limit: number
  offset: number
  employee_id?: string
  entity_type?: string
  date_from?: string
  date_to?: string
  /** FB-108. Matches the actor, the action, the entity kind, its id, or anything in `detail`. */
  search?: string
}

/**
 * The query `/audit-logs` wants.
 *
 * `employee_id`, `entity_type`, `date_from`, `date_to` — snake_case, like `/statistics/*` and unlike
 * `/employees/performance`. The search is applied here rather than sent, because the endpoint has no
 * search parameter and inventing one would silently return the unfiltered page.
 */
export function auditQuery(filter: AuditFilter, page: number): AuditQuery {
  const query: AuditQuery = {
    limit: AUDIT_PAGE_SIZE,
    offset: Math.max(0, (page - 1) * AUDIT_PAGE_SIZE),
  }
  if (filter.employeeId !== 'all') query.employee_id = filter.employeeId
  if (filter.entityType !== 'all') query.entity_type = filter.entityType
  if (filter.dateFrom !== '') query.date_from = filter.dateFrom
  if (filter.dateTo !== '') query.date_to = filter.dateTo
  // FB-108. The search reaches the whole table now, which for this one matters more than most:
  // 79 644 rows at a hundred a page, so a name outside the current page reported *nothing found*
  // for a change that is recorded.
  if (filter.search.trim() !== '') query.search = filter.search.trim()
  return query
}

/**
 * The local narrowing, kept only for the moment between a keystroke and the server's answer.
 *
 * It used to be the whole of the search — over whatever hundred rows had come back — so a name from
 * last month reported *nothing found* for a change that is recorded. `GET /audit-logs?search=` covers
 * all 79 644 rows now, including inside the jsonb `detail`, and its answer replaces this.
 */
export function matchesAuditSearch(row: AuditRow, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  const haystack = [
    row.employeeName,
    row.entityId,
    actionLabel(row.action),
    entityLabel(row.entityType),
    JSON.stringify(row.detail ?? {}),
  ]
  return haystack.some((field) => String(field ?? '').toLowerCase().includes(needle))
}

export interface Paging {
  page: number
  pages: number
  from: number
  to: number
  total: number
}

/** Which slice of the 79 700 is on the screen — legacy shows the page number and not the range. */
export function auditPaging(total: number, page: number, shown: number): Paging {
  const pages = Math.max(1, Math.ceil(total / AUDIT_PAGE_SIZE))
  const from = total === 0 ? 0 : (page - 1) * AUDIT_PAGE_SIZE + 1
  return { page, pages, from, to: from + Math.max(0, shown - 1), total }
}

// ─── what broke ──────────────────────────────────────────────────────────────

export interface SystemLogRow {
  id?: number
  level?: string
  message?: string
  context?: Record<string, unknown> | null
  source?: string | null
  deviceId?: string | null
  createdAt?: string | null
}

export interface SystemListing {
  rows?: SystemLogRow[]
}

export type Level = 'error' | 'warning' | 'info' | 'debug'

/** `warn` and `warning` are the same level and both appear in the data. */
export function normaliseLevel(level: string | undefined): Level {
  const raw = (level ?? '').toLowerCase()
  if (raw === 'error' || raw === 'fatal') return 'error'
  if (raw === 'warn' || raw === 'warning') return 'warning'
  if (raw === 'debug' || raw === 'trace') return 'debug'
  return 'info'
}

export function levelColor(level: string | undefined): BadgeColor {
  const normalised = normaliseLevel(level)
  if (normalised === 'error') return 'red'
  if (normalised === 'warning') return 'amber'
  if (normalised === 'debug') return 'zinc'
  return 'blue'
}

export const LEVELS: readonly Level[] = ['error', 'warning', 'info', 'debug']

export interface LevelCounts {
  error: number
  warning: number
  info: number
  debug: number
  total: number
}

export function levelCounts(rows: SystemLogRow[]): LevelCounts {
  const counts: LevelCounts = { error: 0, warning: 0, info: 0, debug: 0, total: rows.length }
  for (const row of rows) counts[normaliseLevel(row.level)] += 1
  return counts
}

/**
 * A message reduced to its shape.
 *
 * Every number becomes `#` and every quoted or bracketed value a placeholder, so *"Fiskálny doklad
 * vytlačený – Rozvoz (10812, card_courier, 20.0 €)"* and the same line for order 10813 are one thing.
 * On the newest thousand rows this turns 1 000 lines into 57.
 */
export function messageShape(message: string | undefined): string {
  return (message ?? '')
    .replace(/\b\d[\d\s.,:-]*\b/g, '#')
    .replace(/\[[^\]]*\]/g, '[…]')
    .replace(/\s+/g, ' ')
    .trim()
}

export interface LogGroup {
  shape: string
  level: Level
  source: string | null
  count: number
  /** The most recent occurrence, so the raw line is still readable. */
  newest: SystemLogRow
  firstSeen: number | null
  lastSeen: number | null
}

/**
 * The rows grouped by shape, worst level and biggest count first.
 *
 * A flat chronological list buries 59 errors under 926 information lines. Grouping puts the recurring
 * failure at the top and keeps its newest instance to read — which is what somebody opening a page
 * called *what broke* is after.
 */
export function groupLogs(rows: SystemLogRow[]): LogGroup[] {
  const groups = new Map<string, LogGroup>()

  for (const row of rows) {
    const level = normaliseLevel(row.level)
    const shape = messageShape(row.message)
    const key = `${level}|${row.source ?? ''}|${shape}`
    const stamp = parseStamp(row.createdAt)
    const existing = groups.get(key)

    if (existing === undefined) {
      groups.set(key, {
        shape,
        level,
        source: row.source ?? null,
        count: 1,
        newest: row,
        firstSeen: stamp,
        lastSeen: stamp,
      })
      continue
    }

    existing.count += 1
    if (stamp !== null) {
      if (existing.firstSeen === null || stamp < existing.firstSeen) existing.firstSeen = stamp
      if (existing.lastSeen === null || stamp > existing.lastSeen) {
        existing.lastSeen = stamp
        existing.newest = row
      }
    }
  }

  const severity: Record<Level, number> = { error: 0, warning: 1, info: 2, debug: 3 }
  return [...groups.values()].sort(
    (left, right) =>
      severity[left.level] - severity[right.level] ||
      right.count - left.count ||
      (right.lastSeen ?? 0) - (left.lastSeen ?? 0),
  )
}

export interface SystemFilter {
  level: string
  source: string
  since: string
  search: string
}

export const EMPTY_SYSTEM_FILTER: SystemFilter = {
  level: 'all',
  source: 'all',
  since: '',
  search: '',
}

/** How many rows to ask for. There is no offset, so this is the only reach the page has. */
export const SYSTEM_LOG_LIMIT = 500

/**
 * **FB-110.** The ceiling, and it is a cliff rather than a clamp.
 *
 * `limit` is honoured up to 1 000; **above 1 000 the server falls back to its default of 200**, so
 * asking for more returns a fifth as much. Measured on the restored data: `limit=1000` answers 1 000
 * rows reaching back to 14 July, `limit=1001` answers 200 reaching back only to 27 July. Legacy Express
 * does the identical thing — `limitRaw <= 1000 ? limitRaw : 200` — so this is the contract, faithfully
 * kept, and not a port defect. But it means a *show more* control that offers 5 000 quietly shows less
 * than one offering 1 000, so the choices stop at the ceiling and the page says where the ceiling is.
 */
export const SYSTEM_LOG_MAX_LIMIT = 1000

/** The reaches worth offering: the server's own default, a wider one, and the ceiling. */
export const SYSTEM_LOG_REACHES: readonly number[] = [200, 500, SYSTEM_LOG_MAX_LIMIT]

/** Never ask for a number the server will answer by giving less. */
export function cappedLimit(limit: number): number {
  if (!Number.isFinite(limit) || limit <= 0) return SYSTEM_LOG_LIMIT
  return Math.min(Math.floor(limit), SYSTEM_LOG_MAX_LIMIT)
}

/**
 * **FB-111.** The levels the server's filter will actually match.
 *
 * `level` is checked against exactly `info`, `warning` and `error`; anything else is **silently
 * dropped**, and the query then returns every level while the filter says otherwise. So the page offers
 * these three and nothing more — a *Debug* choice would show the whole log and call it debug.
 */
export const FILTERABLE_LEVELS: readonly string[] = ['error', 'warning', 'info']

/**
 * How many of the loaded rows are spelled `warn` rather than `warning`.
 *
 * The table holds both — 81 rows at `warn` against 1 494 at `warning` on the restored data — and the
 * server's whitelist matches only the long spelling, so asking for warnings cannot reach the short one.
 * `normaliseLevel` folds them for display and counting; this counts what the filter would miss, from
 * the rows in hand rather than from a number written down once.
 */
export function shortSpelledWarnings(rows: SystemLogRow[]): number {
  return rows.filter((row) => (row.level ?? '').toLowerCase() === 'warn').length
}

/** Exactly the parameters `GET /api/v1/system-logs` declares — note the absent offset. */
export interface SystemQuery {
  limit: number
  level?: string
  source?: string
  since?: string
  /** Absent means the stack traces come too, exactly as before the parameter existed. */
  context?: boolean
}

/**
 * The query `/system-logs` wants — and what it does not offer.
 *
 * `level`, `source`, `deviceId`, `since`, `limit`. **No offset and no total**, so the page cannot be
 * paged: asking for more rows is the only way to see further back, and the page has to say so rather
 * than implying the list is everything.
 */
/**
 * The query the list polls with.
 *
 * **`context: false` unless a row is open.** Measured 2026-08-08: the response is 3,6 MB and
 * `context` — the stack trace stored on every 500 — is 97,4 % of it, while the page draws it only
 * inside a row somebody expanded. Polling every sixty seconds, that was about 210 MB an hour for
 * something nobody was looking at. Asking for it only while a row is open costs one full fetch on
 * expand and 106 KB a minute the rest of the time.
 */
export function systemQuery(
  filter: SystemFilter,
  limit = SYSTEM_LOG_LIMIT,
  withContext = false,
): SystemQuery {
  const query: SystemQuery = { limit: cappedLimit(limit) }
  // Absent means *the whole thing*, which is what the endpoint answered before the parameter existed.
  if (!withContext) query.context = false
  // Only a level the server matches; sending `debug` or `warn` returns the whole log instead (FB-111).
  if (filter.level !== 'all' && FILTERABLE_LEVELS.includes(filter.level)) query.level = filter.level
  if (filter.source !== 'all') query.source = filter.source
  if (filter.since !== '') query.since = filter.since
  return query
}

/**
 * The level filter, applied again over the rows in hand.
 *
 * The server already filtered by the whitelisted spelling; doing it here as well costs nothing when it
 * did, and is the only filtering there is when it silently declined to (FB-111). `warn` and `warning`
 * fold together, so the short-spelled rows in the window are kept rather than looking like a different
 * level.
 */
export function matchesLevel(row: SystemLogRow, level: string): boolean {
  if (level === 'all') return true
  return normaliseLevel(row.level) === normaliseLevel(level)
}

export function matchesSystemSearch(row: SystemLogRow, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  return [row.message, row.source, row.deviceId, JSON.stringify(row.context ?? {})].some((field) =>
    String(field ?? '').toLowerCase().includes(needle),
  )
}

export function sourceNames(rows: SystemLogRow[]): string[] {
  return [...new Set(rows.map((row) => row.source ?? ''))]
    .filter((source) => source !== '')
    .sort((left, right) => left.localeCompare(right))
}

/** Whether the answer is as long as the ask, which is the only clue that more exists. */
export function mayHaveMore(rows: SystemLogRow[], limit: number): boolean {
  return rows.length >= cappedLimit(limit)
}

// ─── health ──────────────────────────────────────────────────────────────────

export interface Health {
  status?: string
  db?: string
  uptime?: number
  timestamp?: string
}

export function healthColour(health: Health | undefined): BadgeColor {
  if (health?.status === 'ok' && health.db === 'ok') return 'emerald'
  if (health?.status === undefined) return 'zinc'
  return 'red'
}

/** `44396` → `12h 19m 56s`. */
export function formatUptime(seconds: number | undefined): string {
  const total = Math.max(0, Math.floor(seconds ?? 0))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const rest = total % 60
  if (hours > 0) return `${hours}h ${minutes}m ${rest}s`
  if (minutes > 0) return `${minutes}m ${rest}s`
  return `${rest}s`
}

export { formatCount }
